import React, { useContext } from "react";
import { Form, InputNumber, Radio, Checkbox, Button, Tooltip } from "antd";

import { QuestionCircleFilled } from "@ant-design/icons";

import CustomDateTimePicker from "../../common/CustomDateTimePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CKEditorWrapper from "../../common/CKEditorWrapper";
import InfoBar from "../../common/InfoBar";
import { AuthContext } from "../../../context/AuthContext";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Helsinki");

const AuctionFormSaleInfo = ({ auction, setAuction, handleDateChange }) => {
    const { user, hasRole } = useContext(AuthContext);

    const handleEditorChange = (content) => {
        setAuction({ ...auction, description: content });
    };

    const setStartNow = () => {
        setAuction({ ...auction, starts: new Date() });
    };

    const isDateValid = (date) => {
        const startDate = auction.starts ? dayjs(auction.starts) : dayjs();
        const twoMonthsLater = startDate.add(2, "month");
        return dayjs(date).isBefore(twoMonthsLater);
    };

    const isStartDateValid = (date) => {
        return dayjs(date).isAfter(dayjs("2024-10-31"));
    };

    const toggleDescription = () => {
        const description = document.getElementById("descriptionContainer");

        description.classList.toggle("hidden");
    };

    return (
        <>
            <div className="text-xl font-bold mb-2">Ilmoituksen tiedot</div>

            {auction.auctionType === "rent" && (
                <Form.Item
                    label="Vuokrahinta"
                    name="startPrice"
                    rules={[
                        {
                            required: true,
                            message: "Anna vuokrahinta",
                        },
                    ]}
                >
                    <InputNumber
                        addonAfter="€ / vko"
                        value={auction.startPrice}
                        onChange={(value) => setAuction({ ...auction, startPrice: value })}
                        disabled={
                            auction.status === "draft" ||
                            auction.status === "published" ||
                            (auction.status === "" && auction.type === undefined)
                                ? false
                                : true
                        }
                    />
                </Form.Item>
            )}
            {auction.auctionType === "sale" && (
                <Form.Item
                    label="Myyntihinta"
                    name="startPrice"
                    rules={[{ required: true, message: "Anna myyntihinta" }]}
                >
                    <InputNumber
                        value={auction.startPrice}
                        onChange={(value) => setAuction({ ...auction, startPrice: value })}
                        addonAfter="€"
                        disabled={
                            auction.status === "draft" ||
                            auction.status === "published" ||
                            (auction.status === "" && auction.type === undefined)
                                ? false
                                : true
                        }
                    />
                </Form.Item>
            )}
            {auction.auctionType === "auction" && (
                <div className="md:grid md:grid-cols-3 gap-5">
                    <div>
                        <Form.Item name="startPrice" label="Lähtöhinta" required={true} className="m-0">
                            <InputNumber
                                value={auction.startPrice}
                                onChange={(value) => setAuction({ ...auction, startPrice: value })}
                                style={{ width: "100%" }}
                                className="mb-5"
                                addonAfter="€"
                                disabled={
                                    auction.status === "draft" ||
                                    (auction.status === "" && auction.type === undefined) ||
                                    hasRole(user.roles, "ROLE_SUPERADMIN")
                                        ? false
                                        : true
                                }
                            />
                        </Form.Item>
                        <Form.Item>
                            <Checkbox
                                name="minSalePrice"
                                onChange={(e) =>
                                    setAuction({
                                        ...auction,
                                        minSalePrice: e.target.checked ? 1 : 0,
                                    })
                                }
                                checked={auction.minSalePrice > 0 ? true : false}
                                required={true}
                            >
                                Sitoudun myymään eniten tarjoavalle
                            </Checkbox>
                        </Form.Item>
                    </div>
                    <Form.Item name="minBid" label="Minimikorotus" required={true}>
                        <InputNumber
                            value={auction.minBid}
                            onChange={(value) => setAuction({ ...auction, minBid: value })}
                            style={{ width: "100%" }}
                            addonAfter="€"
                            disabled={
                                auction.status === "draft" ||
                                (auction.status === "" && auction.type === undefined) ||
                                hasRole(user.roles, "ROLE_SUPERADMIN")
                                    ? false
                                    : true
                            }
                        />
                    </Form.Item>
                </div>
            )}

            {auction.auctionType === "auction" && (
                <div>
                    <div className="md:grid md:grid-cols-3 gap-x-5">
                        <div className="mb-1">
                            <Form.Item name="starts" label="Myynti alkaa" className="m-0">
                                <CustomDateTimePicker
                                    selectedDate={auction.starts ? dayjs(auction.starts).toDate() : null}
                                    onDateChange={(date) => setAuction({ ...auction, starts: date })}
                                    disabled={auction.bids.bids?.length > 0}
                                    showTimeSelect={true}
                                    filterDate={isStartDateValid}
                                />
                            </Form.Item>
                            {auction.starts === null && (
                                <div
                                    className="underline text-primary cursor-pointer my-2 md:my-0"
                                    onClick={setStartNow}
                                >
                                    Aloita heti
                                </div>
                            )}
                        </div>
                        <div>
                            <Form.Item name="ends" label="Myynti päättyy" className="m-0">
                                <CustomDateTimePicker
                                    selectedDate={auction.ends ? dayjs(auction.ends).toDate() : null}
                                    onDateChange={(date) => setAuction({ ...auction, ends: date })}
                                    filterDate={isDateValid}
                                    disabled={auction.bids.bids?.length > 0}
                                    showTimeSelect={true}
                                />
                            </Form.Item>
                        </div>
                    </div>
                    <div className="text-sm text-gray-600 mb-5">
                        Suosittelemme 30 pv myyntiaikaa. Maksimimyyntiaika on 2 kk.
                    </div>
                </div>
            )}

            {auction.auctionType === "sale" && (
                <Form.Item
                    label={
                        <>
                            Ilmoituksen kesto
                            <Tooltip
                                title={
                                    <>
                                        Ilmoitushinnat:
                                        <br />
                                        <br />6 kk = 49,90€
                                        <br />12 kk = 79,90€
                                    </>
                                }
                            >
                                <div className="text-lg">
                                    <QuestionCircleFilled className="text-blue-700 ml-2" />
                                </div>
                            </Tooltip>
                        </>
                    }
                    name="saleTime"
                    required={true}
                >
                    <Radio.Group
                        disabled={
                            auction.status === "draft" || (auction.status === "" && auction.type === undefined)
                                ? false
                                : true
                        }
                        name="saleTime"
                        value={auction.saleTime}
                        onChange={(e) => setAuction({ ...auction, saleTime: e.target.value })}
                        optionType="button"
                        buttonStyle="solid"
                    >
                        <Radio value={6}>6 kk</Radio>
                        <Radio value={12}>12 kk</Radio>
                    </Radio.Group>
                </Form.Item>
            )}

            <div className="font-bold mb-2">Täydennä ilmoitusta</div>
            {auction.property.description && (
                <InfoBar>Ilmoituksesi on nyt valmis julkaistavaksi, halutessasi voit lisätä tekstiä.</InfoBar>
            )}
            <Button type="primary" className="mb-5" onClick={toggleDescription}>
                Lisää tekstiä
            </Button>

            <div id="descriptionContainer" className="hidden">
                <Form.Item name="description">
                    <CKEditorWrapper
                        content={auction.description}
                        setContent={handleEditorChange}
                        disabled={auction.status === "sold"}
                    />
                </Form.Item>
            </div>
        </>
    );
};

export default AuctionFormSaleInfo;
